// frontend/src/components/staff/ClientRequestsView.jsx
import React, { useState, useEffect } from 'react';
import { api as apiClient } from '../../contexts/authClient';  // ← Ajout important
import { 
  Fuel, Wrench, CheckCircle, XCircle, Clock, AlertCircle,
  Eye, Filter, Search, RefreshCw, User, Calendar, DollarSign,
  MessageSquare, Phone, Mail, ChevronRight, ChevronDown, ChevronUp
} from 'lucide-react';

const ClientRequestsView = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [stats, setStats] = useState({ pending: 0, approved: 0, completed: 0, rejected: 0 });

  useEffect(() => {
    loadRequests();
  }, [filterType, filterStatus]);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType !== 'all') params.request_type = filterType;
      if (filterStatus !== 'all') params.status = filterStatus;
      
      const response = await apiClient.get('/staff/client-requests', { params });  // ← Modifié
      setRequests(response.data.requests || []);
      
      const all = response.data.requests || [];
      setStats({
        pending: all.filter(r => r.status === 'pending').length,
        approved: all.filter(r => r.status === 'approved').length,
        completed: all.filter(r => r.status === 'completed').length,
        rejected: all.filter(r => r.status === 'rejected').length,
      });
    } catch (error) {
      console.error('Error loading requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    setActionLoading(true);
    try {
      await apiClient.put(`/staff/client-requests/${requestId}/approve`, {});  // ← Modifié
      await loadRequests();
    } catch (error) {
      console.error('Error approving request:', error);
      alert('Failed to approve request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (requestId) => {
    if (!rejectReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    setActionLoading(true);
    try {
      await apiClient.put(`/staff/client-requests/${requestId}/reject`, {  // ← Modifié
        reason: rejectReason
      });
      setShowRejectModal(false);
      setRejectReason('');
      await loadRequests();
    } catch (error) {
      console.error('Error rejecting request:', error);
      alert('Failed to reject request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async (requestId) => {
    setActionLoading(true);
    try {
      await apiClient.put(`/staff/client-requests/${requestId}/complete`, {});  // ← Modifié
      await loadRequests();
    } catch (error) {
      console.error('Error completing request:', error);
      alert('Failed to complete request');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: 'Pending', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
      approved: { label: 'Approved', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
      completed: { label: 'Completed', color: 'bg-blue-500/20 text-blue-400', icon: CheckCircle },
      rejected: { label: 'Rejected', color: 'bg-red-500/20 text-red-400', icon: XCircle },
      cancelled: { label: 'Cancelled', color: 'bg-gray-500/20 text-gray-400', icon: XCircle }
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${c.color}`}>
        <Icon className="w-3 h-3" />
        {c.label}
      </span>
    );
  };

  const getTypeIcon = (type) => {
    if (type === 'fuel') return <Fuel className="w-5 h-5 text-orange-400" />;
    return <Wrench className="w-5 h-5 text-red-400" />;
  };

  const getTypeLabel = (type) => {
    return type === 'fuel' ? 'Fuel Request' : 'Maintenance Request';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const filteredRequests = requests.filter(r => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      r.client_first_name?.toLowerCase().includes(searchLower) ||
      r.client_last_name?.toLowerCase().includes(searchLower) ||
      r.client_email?.toLowerCase().includes(searchLower) ||
      r.company_name?.toLowerCase().includes(searchLower) ||
      r.tail_number?.toLowerCase().includes(searchLower) ||
      r.description?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-white">Client Requests</h2>
          <p className="text-gray-400 text-sm">Manage fuel and maintenance requests from clients</p>
        </div>
        <button onClick={loadRequests} className="p-2 hover:bg-gray-700 rounded-lg text-gray-400">
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <p className="text-gray-400 text-sm">Pending</p>
            <Clock className="w-5 h-5 text-yellow-400" />
          </div>
          <p className="text-2xl font-bold text-white mt-1">{stats.pending}</p>
        </div>
        <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <p className="text-gray-400 text-sm">Approved</p>
            <CheckCircle className="w-5 h-5 text-green-400" />
          </div>
          <p className="text-2xl font-bold text-white mt-1">{stats.approved}</p>
        </div>
        <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <p className="text-gray-400 text-sm">Completed</p>
            <CheckCircle className="w-5 h-5 text-blue-400" />
          </div>
          <p className="text-2xl font-bold text-white mt-1">{stats.completed}</p>
        </div>
        <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <p className="text-gray-400 text-sm">Rejected</p>
            <XCircle className="w-5 h-5 text-red-400" />
          </div>
          <p className="text-2xl font-bold text-white mt-1">{stats.rejected}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex gap-2">
          <button
            onClick={() => setFilterType('all')}
            className={`px-3 py-1.5 rounded-lg text-sm transition ${
              filterType === 'all' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilterType('fuel')}
            className={`px-3 py-1.5 rounded-lg text-sm transition flex items-center gap-1 ${
              filterType === 'fuel' ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
            }`}
          >
            <Fuel className="w-4 h-4" /> Fuel
          </button>
          <button
            onClick={() => setFilterType('maintenance')}
            className={`px-3 py-1.5 rounded-lg text-sm transition flex items-center gap-1 ${
              filterType === 'maintenance' ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
            }`}
          >
            <Wrench className="w-4 h-4" /> Maintenance
          </button>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            placeholder="Search by client, aircraft..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm w-64"
          />
        </div>
      </div>

      {/* Status Filter Tabs */}
      <div className="flex gap-2 border-b border-gray-700 pb-2">
        {['all', 'pending', 'approved', 'completed', 'rejected'].map((status) => (
          <button
            key={status}
            onClick={() => setFilterStatus(status)}
            className={`px-3 py-1 rounded-lg text-sm capitalize transition ${
              filterStatus === status
                ? 'bg-blue-500/20 text-blue-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {status}
          </button>
        ))}
      </div>

      {/* Requests List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : filteredRequests.length === 0 ? (
        <div className="text-center py-12 bg-gray-800/50 rounded-xl border border-gray-700">
          <AlertCircle className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500">No requests found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredRequests.map((req) => (
            <div
              key={req.id}
              className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden"
            >
              {/* Header */}
              <div
                className="p-4 cursor-pointer hover:bg-gray-800/70 transition flex items-center justify-between"
                onClick={() => setExpandedId(expandedId === req.id ? null : req.id)}
              >
                <div className="flex items-center gap-4">
                  <div className="p-2 rounded-lg bg-gray-700/50">
                    {getTypeIcon(req.request_type)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-white font-medium">
                        {req.request_type === 'fuel' 
                          ? `${req.quantity || '?'} gallons`
                          : req.tail_number || 'Maintenance'}
                      </h3>
                      {getStatusBadge(req.status)}
                    </div>
                    <p className="text-sm text-gray-400 mt-1">
                      {req.client_first_name} {req.client_last_name}
                      {req.company_name && ` (${req.company_name})`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <p className="text-xs text-gray-500">{formatDate(req.created_at)}</p>
                  {expandedId === req.id ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                </div>
              </div>

              {/* Expanded Details */}
              {expandedId === req.id && (
                <div className="p-4 border-t border-gray-700 bg-gray-900/30">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Description</p>
                      <p className="text-gray-300 text-sm">{req.description || 'No description'}</p>
                    </div>
                    <div className="space-y-2">
                      {req.scheduled_date && (
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Scheduled Date</p>
                          <p className="text-gray-300 text-sm">{formatDate(req.scheduled_date)}</p>
                        </div>
                      )}
                      {req.tail_number && (
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Aircraft</p>
                          <p className="text-gray-300 text-sm">{req.tail_number}</p>
                        </div>
                      )}
                      {req.priority && req.priority !== 'normal' && (
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Priority</p>
                          <p className={`text-sm ${req.priority === 'urgent' ? 'text-red-400' : 'text-orange-400'}`}>
                            {req.priority.toUpperCase()}
                          </p>
                        </div>
                      )}
                      {req.approved_by_first_name && (
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Approved By</p>
                          <p className="text-gray-300 text-sm">{req.approved_by_first_name} {req.approved_by_last_name}</p>
                          <p className="text-xs text-gray-500">{formatDate(req.approved_at)}</p>
                        </div>
                      )}
                      {req.completed_at && (
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Completed At</p>
                          <p className="text-gray-300 text-sm">{formatDate(req.completed_at)}</p>
                        </div>
                      )}
                      {req.notes && (
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Notes</p>
                          <p className="text-gray-300 text-sm">{req.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  {req.status === 'pending' && (
                    <div className="flex gap-3 mt-4 pt-3 border-t border-gray-700">
                      <button
                        onClick={() => handleApprove(req.id)}
                        disabled={actionLoading}
                        className="px-4 py-2 bg-green-500 hover:bg-green-600 rounded-lg text-white text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Approve
                      </button>
                      <button
                        onClick={() => {
                          setSelectedRequest(req);
                          setShowRejectModal(true);
                        }}
                        disabled={actionLoading}
                        className="px-4 py-2 bg-red-500 hover:bg-red-600 rounded-lg text-white text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                      >
                        <XCircle className="w-4 h-4" />
                        Reject
                      </button>
                    </div>
                  )}

                  {req.status === 'approved' && (
                    <div className="flex gap-3 mt-4 pt-3 border-t border-gray-700">
                      <button
                        onClick={() => handleComplete(req.id)}
                        disabled={actionLoading}
                        className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Mark as Completed
                      </button>
                    </div>
                  )}

                  {/* Client Contact Info */}
                  <div className="mt-4 pt-3 border-t border-gray-700">
                    <p className="text-xs text-gray-500 mb-2">Contact Client</p>
                    <div className="flex gap-3">
                      {req.client_email && (
                        <button className="flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300">
                          <Mail className="w-4 h-4" />
                          Email
                        </button>
                      )}
                      <button className="flex items-center gap-1 text-sm text-green-400 hover:text-green-300">
                        <MessageSquare className="w-4 h-4" />
                        Chat
                        </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-700">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-white font-semibold">Reject Request</h3>
              <button onClick={() => setShowRejectModal(false)} className="text-gray-400 hover:text-white">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <label className="block text-sm text-gray-400 mb-2">Reason for rejection *</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows="3"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm resize-none focus:outline-none focus:border-blue-500"
                placeholder="Explain why this request is being rejected..."
              />
            </div>
            <div className="flex gap-3 p-4 border-t border-gray-700">
              <button
                onClick={() => handleReject(selectedRequest?.id)}
                disabled={actionLoading}
                className="flex-1 py-2 bg-red-500 hover:bg-red-600 rounded-lg text-white font-medium disabled:opacity-50"
              >
                {actionLoading ? 'Processing...' : 'Confirm Rejection'}
              </button>
              <button
                onClick={() => setShowRejectModal(false)}
                className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientRequestsView;